// The Help Centre (design S9/S10): the User Guide — contents in Setup order, search, one page at a time,
// and Print / Save as PDF of the whole guide (the browser's print, so it always matches the version in use).
// Lists only the pages of the screens the user can open, plus Getting Started and FAQ.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { helpService, type HelpPage, type HelpPageInfo, type HelpSearchHit } from 'api/services/helpService';
import HelpMarkdown from './HelpMarkdown';
import { visibleHelpPages } from './helpEvents';

interface Props {
  isOpen: boolean;
  initialPageId?: string;
  onClose: () => void;
}

const HelpCentre: React.FC<Props> = ({ isOpen, initialPageId, onClose }) => {
  const [contents, setContents] = useState<HelpPageInfo[]>([]);
  const [pageId, setPageId] = useState<string | undefined>(initialPageId);
  const [page, setPage] = useState<HelpPage | null>(null);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<HelpSearchHit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [printPages, setPrintPages] = useState<HelpPage[] | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPageId(initialPageId);
    setQuery('');
    setHits(null);
    helpService.getContents()
      .then((list) => { setContents(visibleHelpPages(list)); setError(null); })
      .catch(() => setError('Help is not available right now.'));
  }, [isOpen, initialPageId]);

  // No page asked for: the first in the guide (Getting Started).
  const shownId = pageId ?? contents[0]?.id;
  useEffect(() => {
    if (!isOpen || !shownId) { setPage(null); return; }
    let cancelled = false;
    helpService.getPage(shownId).then((p) => { if (!cancelled) setPage(p); }).catch(() => { if (!cancelled) setPage(null); });
    return () => { cancelled = true; };
  }, [isOpen, shownId]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setHits(null); return; }
    const t = window.setTimeout(() => {
      const allowed = new Set(contents.map((c) => c.id));
      helpService.search(q).then((r) => setHits(r.filter((h) => allowed.has(h.id)))).catch(() => setHits([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [query, contents]);

  // Sections in the order of their first page (the server sorts by the guide's order).
  const sections = useMemo(() => {
    const out: { name: string; pages: HelpPageInfo[] }[] = [];
    for (const p of contents) {
      let s = out.find((x) => x.name === p.section);
      if (!s) { s = { name: p.section || 'Other', pages: [] }; out.push(s); }
      s.pages.push(p);
    }
    return out;
  }, [contents]);

  const printGuide = useCallback(async () => {
    const pages = (await Promise.all(contents.map((c) => helpService.getPage(c.id).catch(() => null))))
      .filter((p): p is HelpPage => !!p);
    setPrintPages(pages);
    // Let the print view render, print, then drop it.
    window.setTimeout(() => { window.print(); setPrintPages(null); }, 300);
  }, [contents]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl help-centre" style={{ minHeight: 'auto', height: 'min(88vh, 820px)', maxWidth: 1200 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">User Guide</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void printGuide()} disabled={contents.length === 0}>
              <IconSystem.print size={16} /> Print / Save as PDF
            </button>
            <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><IconSystem.x size={20} /></button>
          </div>
        </div>
        <div className="help-centre-body">
          <nav className="help-centre-nav">
            <input className="form-input" placeholder="Search the guide" value={query} onChange={(e) => setQuery(e.target.value)} />
            {hits ? (
              <div className="help-centre-hits">
                {hits.length === 0 && <div className="setup-sheet-note">Nothing found.</div>}
                {hits.map((h) => (
                  <button key={h.id} type="button" className="help-centre-hit" onClick={() => { setPageId(h.id); setQuery(''); }}>
                    <strong>{h.title}</strong>
                    <span className="setup-sheet-note">{h.snippet}</span>
                  </button>
                ))}
              </div>
            ) : (
              sections.map((s) => (
                <div key={s.name} className="help-centre-section">
                  <div className="config-group-heading">{s.name}</div>
                  {s.pages.map((p) => (
                    <button key={p.id} type="button" className={`help-centre-link${p.id === shownId ? ' active' : ''}`} onClick={() => setPageId(p.id)}>
                      {p.title}
                    </button>
                  ))}
                </div>
              ))
            )}
          </nav>
          <main className="help-centre-page">
            {error && <div className="setup-sheet-note">{error}</div>}
            {!error && !page && contents.length === 0 && <div className="setup-sheet-note">No help pages yet.</div>}
            {page && (
              <>
                <h1 className="help-page-title">
                  {page.title}
                  {page.draft && <span className="badge badge-warning help-draft">Draft</span>}
                </h1>
                <HelpMarkdown markdown={page.markdown} onOpenPage={setPageId} />
              </>
            )}
          </main>
        </div>
      </div>

      {printPages && (
        <div className="help-print">
          <h1>User Guide</h1>
          {printPages.map((p) => (
            <section key={p.id} className="help-print-page">
              <h1 className="help-page-title">{p.title}{p.draft ? ' (draft)' : ''}</h1>
              <HelpMarkdown markdown={p.markdown} onOpenPage={() => undefined} />
            </section>
          ))}
        </div>
      )}
    </>
  );
};

export default HelpCentre;
